import { __ } from '@wordpress/i18n';
import { __experimentalNumberControl as NumberControl } from '@wordpress/components';
import {
	BASE_DEFAULT_VALUE,
	RESET_VALUE,
	STEP,
	SPIN_FACTOR,
	isLineHeightDefined,
} from './utils';
import type {
	LineHeightControlOnChange,
	LineHeightControlProps,
	LineHeightStateReducer,
} from './types';

const LineHeightControl = ( {
	/**
	 * Start opting into the larger default height that will become the default size in a future version.
	 *
	 * @deprecated Default behavior since WordPress 7.1. Prop can be safely removed.
	 */
	__next40pxDefaultSize: _next40pxDefaultSize,
	value: lineHeight,
	onChange,
	__unstableInputWidth = '60px',
	...otherProps
}: LineHeightControlProps ) => {
	const isDefined = isLineHeightDefined( lineHeight );

	const adjustNextValue = (
		nextValue: string | number | undefined,
		wasTypedOrPasted: boolean
	) => {
		// Set the next value without modification if lineHeight has been defined.
		if ( isDefined ) {
			return nextValue;
		}

		/**
		 * The following logic handles the initial spin up/down action
		 * (from an undefined value state) so that the next values are better suited for
		 * line-height rendering. For example, the first spin up should immediately
		 * go to 1.6, rather than the normally expected 0.1.
		 *
		 * Spin up/down actions can be triggered by keydowns of the up/down arrow keys,
		 * dragging the input or by clicking the spin buttons.
		 */
		const spin = STEP * SPIN_FACTOR;
		switch ( `${ nextValue }` ) {
			case `${ spin }`:
				// Increment by spin value.
				return BASE_DEFAULT_VALUE + spin;
			case '0': {
				// This means the user explicitly input '0', rather than using the
				// spin down action from an undefined value state.
				if ( wasTypedOrPasted ) {
					return nextValue;
				}
				// Decrement by spin value.
				return BASE_DEFAULT_VALUE - spin;
			}
			case '':
				return BASE_DEFAULT_VALUE;
			default:
				return nextValue;
		}
	};

	const stateReducer: LineHeightStateReducer = ( state, action ) => {
		// Be careful when changing this — cross-browser behavior of the
		// `inputType` field in `input` events are inconsistent.
		// For example, Firefox emits an input event with inputType="insertReplacementText"
		// on spin button clicks, while other browsers do not even emit an input event.
		const wasTypedOrPasted = [ 'insertText', 'insertFromPaste' ].includes(
			// Change actions carry the input event.
			( action.payload.event!.nativeEvent as InputEvent )?.inputType
		);
		const value = adjustNextValue( state.value, wasTypedOrPasted );
		// The input takes the numeric spin values as they are.
		return { ...state, value: value as string | undefined };
	};

	const value = isDefined ? lineHeight : RESET_VALUE;

	const handleOnChange: LineHeightControlOnChange = (
		nextValue,
		{ event }
	) => {
		if ( nextValue === '' ) {
			onChange();
			return;
		}

		if ( event.type === 'click' ) {
			onChange( adjustNextValue( `${ nextValue }`, false ) );
			return;
		}

		onChange( `${ nextValue }` );
	};

	return (
		<div className="block-editor-line-height-control">
			<NumberControl
				// The input renders the numeric placeholder as text.
				placeholder={ BASE_DEFAULT_VALUE as unknown as string }
				{ ...otherProps }
				__unstableInputWidth={ __unstableInputWidth }
				__unstableStateReducer={ stateReducer }
				onChange={ handleOnChange }
				label={ __( 'Line height' ) }
				step={ STEP }
				spinFactor={ SPIN_FACTOR }
				value={ value }
				min={ 0 }
				spinControls="custom"
			/>
		</div>
	);
};

/**
 * @see https://github.com/WordPress/gutenberg/blob/HEAD/packages/block-editor/src/components/line-height-control/README.md
 */
export default LineHeightControl;
